import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
	getScannerRequests,
	getSpecificSidebarRequest,
} from "../../../components/Api/ExamGradingEndpoints";
import { useAtom } from "jotai";
import { omrRequestsAtom, currentOmrRequestAtom } from "../../../components/Atoms/atoms";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import RefreshIcon from "@mui/icons-material/Refresh";
import { CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

const isSixDigitReqnum = (value) => /^\d{6}$/.test(String(value).trim());
const isNumericOnly = (value) => /^\d+$/.test(String(value).trim());
const PROGRAM_FILTER_OPTIONS = [
	{
		value: "1",
		label: "GPScor",
		selectedClassName: "bg-secondaryIndigo text-white border-secondaryIndigo",
		unselectedClassName:
			"bg-secondaryIndigo/10 text-secondaryIndigo border-secondaryIndigo/30 hover:bg-secondaryIndigo/20",
	},
	{
		value: "2",
		label: "MRScor",
		selectedClassName: "bg-secondaryRed text-white border-secondaryRed",
		unselectedClassName:
			"bg-secondaryRed/10 text-secondaryRed border-secondaryRed/30 hover:bg-secondaryRed/20",
	},
	{
		value: "4",
		label: "Assign2",
		selectedClassName: "bg-secondaryPurple text-white border-secondaryPurple",
		unselectedClassName:
			"bg-secondaryPurple/10 text-secondaryPurple border-secondaryPurple/30 hover:bg-secondaryPurple/20",
	},
];
const STATUS_FILTER_OPTIONS = [
	{
		value: "waiting",
		label: "Waiting",
		selectedClassName: "bg-gray-500 text-white border-gray-500",
		unselectedClassName: "bg-gray-500/10 text-gray-500 border-gray-500/30 hover:bg-gray-500/20",
	},
	{
		value: "loaded",
		label: "Loaded",
		selectedClassName: "bg-secondaryTeal text-white border-secondaryTeal",
		unselectedClassName:
			"bg-secondaryTeal/10 text-secondaryTeal border-secondaryTeal/30 hover:bg-secondaryTeal/20",
	},
	{
		value: "sent",
		label: "Sent",
		selectedClassName: "bg-secondaryLime text-white border-secondaryLime",
		unselectedClassName:
			"bg-secondaryLime/10 text-secondaryLime border-secondaryLime/30 hover:bg-secondaryLime/20",
	},
];
const SIDEBAR_MODE_OPTIONS = [
	{ value: "docked", label: "Side" },
	{ value: "slide", label: "Slide" },
	{ value: "dialog", label: "Dialog" },
];

export default function ExamViewSideBar(props) {
	const {
		token,
		setFile,
		setAlignment,
		setSnackbar,
		editMode,
		access,
		ccid,
		sidebarOpen,
		setSidebarOpen,
		refreshDisabled = false,
		keepSidebarOpen,
		setKeepSidebarOpen,
		sidebarMode = "docked",
		setSidebarMode,
		sidebarWidth = 420,
		onResizeStart,
		onDragStart,
		onResetFloatingPosition,
		isFloatingSidebar = false,
		shellClassName = "",
		shellStyle = {},
	} = props;
	const [currentOmrRequest, setCurrentOmrRequest] = useAtom(currentOmrRequestAtom);
	const [omrRequests, setOmrRequests] = useAtom(omrRequestsAtom);
	const [error, setError] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchMode, setSearchMode] = useState(false);
	const [searchResults, setSearchResults] = useState([]);
	const [textSearchRequests, setTextSearchRequests] = useState([]);
	const [textSearchLoaded, setTextSearchLoaded] = useState(false);
	const [selectedProgramFilter, setSelectedProgramFilter] = useState("");
	const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
	const [refreshingList, setRefreshingList] = useState(false);

	const currentRequestRef = useRef(null);
	const sidebarShellRef = useRef(null);
	const lastScrolledReqnumRef = useRef(null);
	const isAdmin = access.includes("ADMIN");
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [isViewportPinned, setIsViewportPinned] = useState(false);
	const normalizeRequestForSelection = useCallback((reqObj) => {
		if (!reqObj) return reqObj;
		const rawClassIds = String(reqObj.class_ids || "").trim();
		const effectiveClassIds = String(reqObj.class_ids_effective || "").trim();
		const normalizedClassIds =
			rawClassIds ||
			(effectiveClassIds ? effectiveClassIds.split(",").slice(2).join(",").trim() : "");
		return {
			...reqObj,
			class_ids: normalizedClassIds,
		};
	}, []);
	const mergeRequestIntoCollection = useCallback(
		(requests, nextRequest) => {
			if (!Array.isArray(requests) || !nextRequest?.reqnum) return requests;

			let hasMatch = false;
			const merged = requests.map((reqObj) => {
				if (String(reqObj?.reqnum) !== String(nextRequest.reqnum)) {
					return reqObj;
				}
				hasMatch = true;
				return normalizeRequestForSelection({
					...reqObj,
					...nextRequest,
				});
			});

			return hasMatch ? merged : requests;
		},
		[normalizeRequestForSelection],
	);
	const getStatusFilterValue = useCallback((reqObj) => {
		const normalizedStatus = String(reqObj?.status || "")
			.trim()
			.toLowerCase();
		if (normalizedStatus === "received") return "waiting";
		if (normalizedStatus === "loaded") return "loaded";
		if (normalizedStatus === "sent") return "sent";
		return "";
	}, []);

	const getProgramMeta = useCallback((programValue) => {
		const program = String(programValue || "").trim();
		if (program === "1") {
			return {
				label: "GPScor",
				className: "bg-secondaryIndigo/10 text-secondaryIndigo border-secondaryIndigo/30",
			};
		}
		if (program === "2") {
			return {
				label: "MRScor",
				className: "bg-secondaryRed/10 text-secondaryRed border-secondaryRed/30",
			};
		}
		return {
			label: "Assign2",
			className: "bg-secondaryPurple/10 text-secondaryPurple border-secondaryPurple/30",
		};
	}, []);

	const getStatusMeta = useCallback((reqObj) => {
		const program = String(reqObj?.program || "").trim();
		if (program === "4") {
			return reqObj?.tsqs_upload === 1
				? {
						label: "TSQS Upload",
						className: "bg-secondaryTeal/10 text-secondaryTeal border-secondaryTeal/30",
					}
				: {
						label: "Send to Client",
						className: "bg-gray-500/10 text-gray-500 border-gray-500/30",
					};
		}

		const status = String(reqObj?.status || "").trim();
		if (status === "Received") {
			return {
				label: "Waiting for File",
				className: "bg-gray-500/10 text-gray-500 border-gray-500/30",
			};
		}
		if (status === "Loaded") {
			return {
				label: "Loaded",
				className: "bg-secondaryTeal/10 text-secondaryTeal border-secondaryTeal/30",
			};
		}
		if (status === "Sent") {
			return {
				label: "Sent",
				className: "bg-secondaryLime/10 text-secondaryLime border-secondaryLime/30",
			};
		}
		return {
			label: status || "Unknown",
			className: "bg-gray-100 text-gray-700 border-gray-300",
		};
	}, []);

	// ─── pagination state ───────────────────────────────
	const [currentPage, setCurrentPage] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [hasNext, setHasNext] = useState(false);
	const pageSize = 15;
	const [loading, setLoading] = useState(false);
	const [initialLoad, setInitialLoad] = useState(true);

	// ─── fetch one page ──────────────────────────────────
	const fetchRequests = useCallback(
		async (pageNum = 0, isSearch = false) => {
			if (loading) return;
			setLoading(true);
			setError(null);

			try {
				const response = await getScannerRequests(token, pageNum, pageSize);
				const { results, pagination } = response;

				if (isSearch || pageNum === 0) {
					// Replace requests for new search or first page
					if (results.length > 0) {
						const resultsWithNoClassIds = results.filter(
							(item) => !item.class_ids || item.class_ids.trim() === "",
						);
						const resultsWithClassIds = results.filter(
							(item) => item.class_ids && item.class_ids.trim() !== "",
						);
						if (resultsWithNoClassIds.length > 0) {
							// check if class_ids_effective exists, if so, set that to class_ids
							resultsWithNoClassIds.forEach((item) => {
								if (item.class_ids_effective && item.class_ids_effective.trim() !== "") {
									const normalizedClassid = item.class_ids_effective.split(",").slice(2).join(","); // remove W,SR,
									item.class_ids = normalizedClassid;
								}
							});
						}
						const resultsModified = [...resultsWithClassIds, ...resultsWithNoClassIds];
						//sort by reqnum desc
						resultsModified.sort((a, b) => b.reqnum - a.reqnum);
						setOmrRequests(resultsModified);
					} else {
						setOmrRequests([]);
					}
				} else {
					// Append for pagination

					setOmrRequests((prev) => {
						const newItems = results.filter((item) => !prev.some((p) => p.reqnum === item.reqnum));
						const newItemsNoClassIds = newItems.filter(
							(item) => !item.class_ids || item.class_ids.trim() === "",
						);
						const newItemsWithClassIds = newItems.filter(
							(item) => item.class_ids && item.class_ids.trim() !== "",
						);
						if (newItemsNoClassIds.length > 0) {
							// check if class_ids_effective exists, if so, set that to class_ids
							newItemsNoClassIds.forEach((item) => {
								if (item.class_ids_effective && item.class_ids_effective.trim() !== "") {
									const normalizedClassid = item.class_ids_effective.split(",").slice(2).join(","); // remove W,SR,
									item.class_ids = normalizedClassid;
								}
							});
						}
						const newItemsModified = [...newItemsWithClassIds, ...newItemsNoClassIds];
						//sort by reqnum desc
						newItemsModified.sort((a, b) => b.reqnum - a.reqnum);

						return [...prev, ...newItemsModified];
					});
				}

				// Update pagination state
				setCurrentPage(pagination.page);
				setTotalPages(pagination.total_pages);
				setTotalCount(pagination.total_count);
				setHasNext(pagination.has_next);
			} catch (err) {
				console.error("Error fetching omrRequests:", err);
				setError("Failed to load requests. Please try again.");
			} finally {
				setLoading(false);
				setInitialLoad(false);
			}
		},
		[token, pageSize, loading, setOmrRequests],
	);

	// ─── handle specific request search ────────────────────────
	const handleSearchSpecificRequest = useCallback(
		async (reqnum) => {
			if (!isSixDigitReqnum(reqnum)) return;

			setLoading(true);
			setError(null);

			try {
				const response = await getSpecificSidebarRequest(token, reqnum);

				if (response) {
					setSearchResults([response]);
					setSearchMode(true);
					setCurrentOmrRequest(normalizeRequestForSelection(response));
					if (!keepSidebarOpen) {
						setSidebarOpen(false);
					}

					setError(null);
					navigate({ search: `?reqnum=${response.reqnum}` }, { replace: true });
				} else {
					setError("Request not found.");
					setSearchResults([]);
				}
			} catch (err) {
				console.error("Error fetching specific request:", err);
				setError("Failed to find request. Please try again.");
				setSearchResults([]);
			} finally {
				setLoading(false);
			}
		},
		[
			token,
			setCurrentOmrRequest,
			navigate,
			normalizeRequestForSelection,
			setSidebarOpen,
			keepSidebarOpen,
		],
	);

	const fetchRecentTextSearchRequests = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await getScannerRequests(token, 0, 100);
			const recentResults = Array.isArray(response?.results) ? response.results : [];

			const normalizedResults = recentResults
				.map((item) => {
					if (
						(!item.class_ids || item.class_ids.trim() === "") &&
						item.class_ids_effective &&
						item.class_ids_effective.trim() !== ""
					) {
						return {
							...item,
							class_ids: item.class_ids_effective.split(",").slice(2).join(","),
						};
					}
					return item;
				})
				.sort((a, b) => b.reqnum - a.reqnum);

			const scopedResults = isAdmin
				? normalizedResults
				: normalizedResults.filter((req) => String(req.userid).trim() === String(ccid).trim());

			setTextSearchRequests(scopedResults);
			setTextSearchLoaded(true);
		} catch (err) {
			console.error("Error fetching recent requests for text search:", err);
			setError("Failed to search recent requests. Please try again.");
			setTextSearchRequests([]);
			setTextSearchLoaded(false);
		} finally {
			setLoading(false);
		}
	}, [token, isAdmin, ccid]);

	// ─── clear search and return to normal view ─────────────────
	const clearSearch = useCallback(() => {
		setSearchQuery("");
		setSearchMode(false);
		setSearchResults([]);
		setTextSearchRequests([]);
		setTextSearchLoaded(false);
		setError(null);
		fetchRequests(0, true);
	}, [fetchRequests]);

	const handleProgramFilterToggle = useCallback((programValue) => {
		setSelectedProgramFilter((prev) => (prev === programValue ? "" : programValue));
	}, []);

	const handleStatusFilterToggle = useCallback((statusValue) => {
		setSelectedStatusFilter((prev) => (prev === statusValue ? "" : statusValue));
	}, []);

	const clearQuickFilters = useCallback(() => {
		setSelectedProgramFilter("");
		setSelectedStatusFilter("");
	}, []);

	const handleRefreshRequestList = useCallback(async () => {
		if (refreshDisabled || refreshingList || loading) return;
		setRefreshingList(true);
		try {
			await fetchRequests(0, true);

			const trimmedQuery = String(searchQuery || "").trim();
			if (!searchMode && trimmedQuery && !isNumericOnly(trimmedQuery)) {
				await fetchRecentTextSearchRequests();
			}

			if (searchMode) {
				const activeReqnum = String(
					searchResults?.[0]?.reqnum || currentOmrRequest?.reqnum || "",
				).trim();
				if (isSixDigitReqnum(activeReqnum)) {
					const refreshedRequest = await getSpecificSidebarRequest(token, activeReqnum);
					if (refreshedRequest) {
						setSearchResults([refreshedRequest]);
					}
				}
			}
		} catch (err) {
			console.error("Error refreshing sidebar request list:", err);
			setError("Failed to refresh requests. Please try again.");
		} finally {
			setRefreshingList(false);
		}
	}, [
		refreshDisabled,
		refreshingList,
		loading,
		fetchRequests,
		searchQuery,
		searchMode,
		fetchRecentTextSearchRequests,
		searchResults,
		currentOmrRequest?.reqnum,
		token,
	]);

	// ─── detect scroll to bottom ────────────────────────
	const handleScroll = useCallback(
		async (e) => {
			if (searchMode || !hasNext) return; // Don't paginate during search or if no more pages

			const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
			if (scrollHeight - scrollTop <= clientHeight + 50 && !loading) {
				fetchRequests(currentPage + 1, false);
			}
		},
		[searchMode, loading, currentPage, hasNext, fetchRequests],
	);

	// ─── initial load ───────────────────────────────────
	useEffect(() => {
		if (initialLoad) {
			const reqnumFromUrl = searchParams.get("reqnum");

			// If there's a specific reqnum in URL, fetch it directly first
			if (reqnumFromUrl && reqnumFromUrl.length === 6) {
				getSpecificSidebarRequest(token, reqnumFromUrl)
					.then((response) => {
						if (response) {
							setCurrentOmrRequest(normalizeRequestForSelection(response));
						}
					})
					.catch((err) => {
						console.error("Error fetching specific request on mount:", err);
					});
			}

			// Always load the regular list for browsing
			fetchRequests(0, true);
		}
	}, [
		fetchRequests,
		initialLoad,
		searchParams,
		token,
		setCurrentOmrRequest,
		normalizeRequestForSelection,
	]);

	// ─── determine which requests to display ─────────────────────────────────
	const displayRequests = useMemo(() => {
		let baseRequests = [];
		if (searchMode) {
			baseRequests = searchResults;
		} else {
			const trimmedQuery = String(searchQuery || "").trim();
			if (!trimmedQuery) {
				baseRequests = isAdmin
					? omrRequests
					: omrRequests.filter((req) => String(req.userid).trim() === String(ccid).trim());
			} else {
				const q = trimmedQuery.toLowerCase();

				// String input: search instructor/course against latest 100 records.
				if (!isNumericOnly(trimmedQuery)) {
					if (!textSearchLoaded) return [];
					baseRequests = textSearchRequests.filter((reqObj) => {
						return (
							(reqObj.coursetitle || "").toLowerCase().includes(q) ||
							(reqObj.instructor_name || "").toLowerCase().includes(q)
						);
					});
				} else {
					// Numeric input: keep existing local numeric behavior.
					baseRequests = omrRequests.filter((reqObj) => {
						const matchesSearch =
							reqObj.reqnum.toString().includes(q) ||
							(reqObj.program || "").toLowerCase().includes(q) ||
							(reqObj.coursetitle || "").toLowerCase().includes(q) ||
							(reqObj.instructor_name || "").toLowerCase().includes(q);

						return isAdmin
							? matchesSearch
							: matchesSearch && String(reqObj.userid).trim() === String(ccid).trim();
					});
				}
			}
		}

		return baseRequests.filter((reqObj) => {
			const requestProgram = String(reqObj?.program || "").trim();
			const matchesProgram = !selectedProgramFilter || requestProgram === selectedProgramFilter;
			const matchesStatus =
				!selectedStatusFilter || getStatusFilterValue(reqObj) === selectedStatusFilter;
			return matchesProgram && matchesStatus;
		});
	}, [
		searchMode,
		searchResults,
		searchQuery,
		omrRequests,
		isAdmin,
		ccid,
		textSearchLoaded,
		textSearchRequests,
		selectedProgramFilter,
		selectedStatusFilter,
		getStatusFilterValue,
	]);

	// Load recent requests for string search once the query is present.
	useEffect(() => {
		const trimmedQuery = String(searchQuery || "").trim();
		if (!trimmedQuery || searchMode || textSearchLoaded || isNumericOnly(trimmedQuery) || loading) {
			return;
		}
		fetchRecentTextSearchRequests();
	}, [searchQuery, searchMode, textSearchLoaded, loading, fetchRecentTextSearchRequests]);

	// ─── preserve selection ────────────────────────────
	useEffect(() => {
		const reqnumFromUrl = searchParams.get("reqnum");
		if (reqnumFromUrl && omrRequests.length) {
			const foundRequest = omrRequests.find((reqObj) => reqObj.reqnum === Number(reqnumFromUrl));
			if (foundRequest && currentOmrRequest?.reqnum !== foundRequest.reqnum) {
				setCurrentOmrRequest(normalizeRequestForSelection(foundRequest));
				setFile(null); // Reset file when changing request
			}
		} else if (omrRequests.length && !currentOmrRequest?.reqnum) {
			setCurrentOmrRequest(normalizeRequestForSelection(omrRequests[0]));
			// Set the first request in URL if no reqnum is specified
			navigate({ search: `?reqnum=${omrRequests[0].reqnum}` }, { replace: true });
		}
	}, [
		omrRequests,
		currentOmrRequest?.reqnum,
		setCurrentOmrRequest,
		setFile,
		searchParams,
		navigate,
		normalizeRequestForSelection,
	]);

	useEffect(() => {
		const currentReqnum = currentOmrRequest?.reqnum ?? null;
		if (!currentReqnum) {
			lastScrolledReqnumRef.current = null;
			return;
		}

		if (lastScrolledReqnumRef.current === currentReqnum) {
			return;
		}

		if (currentRequestRef.current) {
			currentRequestRef.current.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
			lastScrolledReqnumRef.current = currentReqnum;
		}
	}, [currentOmrRequest?.reqnum]);

	useEffect(() => {
		if (!currentOmrRequest?.reqnum) return;

		const normalizedCurrentRequest = normalizeRequestForSelection(currentOmrRequest);
		setSearchResults((prev) => mergeRequestIntoCollection(prev, normalizedCurrentRequest));
		setTextSearchRequests((prev) =>
			mergeRequestIntoCollection(prev, normalizedCurrentRequest),
		);
	}, [
		currentOmrRequest,
		normalizeRequestForSelection,
		mergeRequestIntoCollection,
		setSearchResults,
		setTextSearchRequests,
	]);

	useEffect(() => {
		const shell = sidebarShellRef.current;
		if (!shell) return;

		let frameId = null;
		const updatePinnedState = () => {
			frameId = null;
			const rect = shell.getBoundingClientRect();
			const nextPinned = window.scrollY > 0 && rect.top <= 0.5;
			setIsViewportPinned((prev) => (prev === nextPinned ? prev : nextPinned));
		};

		const queueUpdate = () => {
			if (frameId !== null) return;
			frameId = window.requestAnimationFrame(updatePinnedState);
		};

		queueUpdate();
		window.addEventListener("scroll", queueUpdate, { passive: true });
		window.addEventListener("resize", queueUpdate);

		return () => {
			if (frameId !== null) {
				window.cancelAnimationFrame(frameId);
			}
			window.removeEventListener("scroll", queueUpdate);
			window.removeEventListener("resize", queueUpdate);
		};
	}, []);

	const handleRequestSelection = useCallback(
		(reqObj) => {
			if (editMode) {
				setSnackbar({
					open: true,
					message: "You must save or discard answer key changes before selecting a new request.",
					severity: "warning",
				});
				return;
			}
			setCurrentOmrRequest(normalizeRequestForSelection(reqObj));
			setFile(null); // Reset file when changing request
			setAlignment("exam-info");
			if (!keepSidebarOpen) {
				setSidebarOpen(false);
			}

			navigate({ search: `?reqnum=${reqObj.reqnum}` }, { replace: true });
		},
		[
			editMode,
			setSnackbar,
			setCurrentOmrRequest,
			setFile,
			setAlignment,
			navigate,
			normalizeRequestForSelection,
			setSidebarOpen,
			keepSidebarOpen,
		],
	);

	const handleChangeSearch = useCallback(
		(e) => {
			const value = e.target.value;
			const trimmedValue = String(value || "").trim();
			setSearchQuery(value);

			// Clear search mode if query is empty
			if (!trimmedValue) {
				clearSearch();
				return;
			}

			// If it's exactly 6 digits, search for specific request
			if (isSixDigitReqnum(trimmedValue)) {
				handleSearchSpecificRequest(trimmedValue);
			} else {
				// Clear specific-request mode for non-6-digit search.
				setSearchMode(false);
				setSearchResults([]);
				setError(null);

				// For string input, load latest 100 requests once, then filter locally.
				if (!isNumericOnly(trimmedValue) && !textSearchLoaded && !loading) {
					fetchRecentTextSearchRequests();
				}
			}
		},
		[
			clearSearch,
			handleSearchSpecificRequest,
			fetchRecentTextSearchRequests,
			textSearchLoaded,
			loading,
		],
	);

	const handleKeyDown = useCallback(
		(e) => {
			if (e.key === "Enter") {
				const query = String(searchQuery || "").trim();
				if (isSixDigitReqnum(query)) {
					const reqnum = query;
					handleSearchSpecificRequest(reqnum);
				} else if (query && !isNumericOnly(query) && !textSearchLoaded && !loading) {
					fetchRecentTextSearchRequests();
				}
				e.preventDefault();
			}
		},
		[
			searchQuery,
			handleSearchSpecificRequest,
			fetchRecentTextSearchRequests,
			textSearchLoaded,
			loading,
		],
	);

	const trimmedSearchQuery = String(searchQuery || "").trim();
	const isStringSearch = Boolean(trimmedSearchQuery) && !isNumericOnly(trimmedSearchQuery);
	const isLoadingStringSearch = isStringSearch && !textSearchLoaded && loading && !searchMode;
	const hasQuickFilters = Boolean(selectedProgramFilter || selectedStatusFilter);
	const isSidebarRefreshDisabled = Boolean(refreshDisabled || refreshingList || loading);
	const sidebarHeight = isViewportPinned ? "100dvh" : "74dvh";

	return (
		<div
			ref={sidebarShellRef}
			className={`${shellClassName} w-full relative flex flex-col overflow-hidden rounded-l bg-digitalWhite transition-[height] duration-300 ease-out ${
				sidebarOpen ? " border-gray-300 border-l-2 border-y-2" : " border-transparent"
			} drop-shadow-2xl shadow-2xl`}
			style={{ height: sidebarHeight, ...shellStyle }}
		>
			{sidebarOpen && onResizeStart && (
				<div
					role="presentation"
					onMouseDown={onResizeStart}
					className="absolute left-0 top-0 z-40 h-full w-2 cursor-ew-resize bg-transparent hover:bg-primary/10"
					aria-hidden="true"
				/>
			)}
			{!sidebarOpen && (
				<button
					type="button"
					onClick={() => setSidebarOpen(true)}
					className="absolute w-full h-full top-0 right-0 bg-digitalWhite z-30   rounded-l border-l-2 border-y-2 hover:bg-gray-300 border-primary"
					aria-label="Open requests sidebar"
				>
					<div className={` w-full   flex flex-col items-center justify-center h-40`}>
						<ManageSearchIcon color="primary" />

						<div className="flex  text-sm tracking-wide text-primary font-bold rotate-90 text-center my-auto">
							REQUESTS
						</div>
					</div>
				</button>
			)}
			{sidebarOpen && (
				<div className="border-b border-gray-300 px-4 py-2">
					<div className="flex items-center justify-between gap-2">
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={keepSidebarOpen}
									onChange={(e) => setKeepSidebarOpen(e.target.checked)}
									className="accent-primary "
									sx={{
										py: 0,
										pr: 0,
										pl: 1,
										"&.MuiCheckbox-root": { color: "primary" },
										"&:hover": {
											backgroundColor: "transparent",
										},
									}}
								/>
							}
							label={<span className="text-xxs md:text-xs">Keep Sidebar Open</span>}
							className="flex items-center gap-2 text-xxs md:text-xs text-gray-700 select-none "
						/>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={handleRefreshRequestList}
								disabled={isSidebarRefreshDisabled}
								className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xxs md:text-xs font-medium transition-colors ${
									isSidebarRefreshDisabled
										? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
										: "border-primary/30 bg-digitalWhite text-primary hover:bg-primary/20"
								}`}
							>
								{refreshingList ? (
									<CircularProgress size={12} color="inherit" />
								) : (
									<RefreshIcon sx={{ fontSize: 14 }} />
								)}
								Refresh
							</button>
							<button
								type="button"
								onClick={() => setSidebarOpen(false)}
								className="inline-flex items-center rounded border border-gray-300 px-2 py-1 text-xxs md:text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
							>
								Close
							</button>
						</div>
					</div>
					{/* <div className="mt-2 flex flex-wrap items-center gap-2">
						<div className="inline-flex rounded-full border border-gray-300 bg-white p-0.5">
							{SIDEBAR_MODE_OPTIONS.map((option) => {
								const isSelected = sidebarMode === option.value;
								return (
									<button
										key={option.value}
										type="button"
										onClick={() => setSidebarMode(option.value)}
										className={`rounded-full px-2.5 py-1 text-xxs md:text-xs font-medium transition-colors ${
											isSelected ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-100"
										}`}
									>
										{option.label}
									</button>
								);
							})}
						</div>
						<button
							type="button"
							onMouseDown={onDragStart}
							className={`rounded border px-2 py-1 text-xxs md:text-xs font-medium ${
								isFloatingSidebar
									? "border-primary/30 text-primary hover:bg-primary/10 cursor-move"
									: "border-gray-200 text-gray-400 cursor-not-allowed"
							}`}
							disabled={!isFloatingSidebar}
						>
							Drag Horizontally
						</button>
						<button
							type="button"
							onClick={onResetFloatingPosition}
							className={`rounded border px-2 py-1 text-xxs md:text-xs font-medium ${
								isFloatingSidebar
									? "border-gray-300 text-gray-700 hover:bg-gray-100"
									: "border-gray-200 text-gray-400 cursor-not-allowed"
							}`}
							disabled={!isFloatingSidebar}
						>
							Reset Position
						</button>
						<span className="text-xxs md:text-xs text-gray-500">{sidebarWidth}px wide</span>
					</div> */}
				</div>
			)}
			{sidebarOpen && (
				<div className=" w-full z-10 border-b  border-gray-300 px-2 py-2 md:px-4 md:pt-2">
					<div className={`flex items-start gap-2 justify-between`}>
						<div>
							<p className="text-xxs md:text-xs uppercase tracking-wide text-gray-500">Program</p>
							<div className="mt-1 flex overflow-x-auto pb-1 justify-end">
								{PROGRAM_FILTER_OPTIONS.map((option, index) => {
									const isSelected = selectedProgramFilter === option.value;
									return (
										<button
											key={option.value}
											type="button"
											onClick={() => handleProgramFilterToggle(option.value)}
											className={`px-3 py-1 text-xxs md:text-xs ${
												index === 0
													? "rounded-l-full"
													: index === PROGRAM_FILTER_OPTIONS.length - 1
														? "rounded-r-full"
														: "rounded-none"
											} border whitespace-nowrap transition-colors ${
												isSelected ? option.selectedClassName : option.unselectedClassName
											}`}
										>
											{option.label}
										</button>
									);
								})}
							</div>
						</div>

						<div>
							<p className="text-xxs md:text-xs uppercase tracking-wide text-gray-500">Status</p>
							<div className="mt-1 flex overflow-x-auto pb-1">
								{STATUS_FILTER_OPTIONS.map((option, index) => {
									const isSelected = selectedStatusFilter === option.value;
									return (
										<button
											key={option.value}
											type="button"
											onClick={() => handleStatusFilterToggle(option.value)}
											className={`px-3 py-1 text-xxs md:text-xs ${
												index === 0
													? "rounded-l-full"
													: index === STATUS_FILTER_OPTIONS.length - 1
														? "rounded-r-full"
														: "rounded-none"
											} border whitespace-nowrap transition-colors ${
												isSelected ? option.selectedClassName : option.unselectedClassName
											}`}
										>
											{option.label}
										</button>
									);
								})}
							</div>
						</div>

						<button
							type="button"
							onClick={clearQuickFilters}
							className={`text-xs my-auto text-gray-600 hover:text-gray-800 ${hasQuickFilters ? "opacity-100" : "opacity-0 pointer-events-none"}`}
						>
							<ClearIcon sx={{ fontSize: 30 }} />
						</button>
					</div>
				</div>
			)}

			{sidebarOpen && (
				<div className=" w-full z-10 border-b  border-gray-300 px-2 py-2 md:px-4 md:pt-2">
					<div className="relative">
						<input
							type="text"
							value={searchQuery}
							onChange={handleChangeSearch}
							onKeyDown={handleKeyDown}
							placeholder="Search requests or enter 6-digit request number..."
							className="w-full px-3 py-2  border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:border-blue-300"
						/>
						{(searchQuery || searchMode) && (
							<button
								onClick={clearSearch}
								className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
							>
								Clear
							</button>
						)}
					</div>
					{searchMode && (
						<div className="mt-2 text-sm text-blue-600">
							Showing specific request #{searchQuery}
						</div>
					)}
					{isStringSearch && (
						<div className="mt-2 text-sm text-blue-600">
							Searching instructor/course title in the latest 100 requests.
						</div>
					)}
				</div>
			)}

			<div
				className={`relative w-full flex-1 min-h-0 overflow-x-hidden ${
					sidebarOpen ? "overflow-y-auto" : "overflow-y-hidden"
				}`}
				onScroll={handleScroll}
			>
				<div
					className={`p-4 transition-all duration-200 ease-in-out ${
						sidebarOpen
							? "opacity-100 translate-x-0"
							: "opacity-0 -translate-x-2 pointer-events-none"
					}`}
				>
					{/* Search Bar */}

					{/* Request List */}
					<div className="space-y-2">
						{error && (
							<div className="text-red-500 text-center mb-4 p-2 bg-red-50 rounded">{error}</div>
						)}

						{(initialLoad && loading) || isLoadingStringSearch ? (
							<div className="text-center text-gray-500 py-8">Loading requests...</div>
						) : displayRequests.length > 0 ? (
							<>
								{displayRequests.map((reqObj) => {
									const isSelected =
										currentOmrRequest && reqObj.reqnum === currentOmrRequest.reqnum;
									const programMeta = getProgramMeta(reqObj.program);
									const statusMeta = getStatusMeta(reqObj);
									return (
										<button
											key={reqObj.reqnum}
											ref={isSelected ? currentRequestRef : null}
											className={`relative w-full rounded-md px-3 py-2 text-left border transition-all ${
												isSelected
													? "border-primary bg-primary/5 shadow-md ring-2 ring-primary/50"
													: "border-gray-200 bg-white  hover:ring-2 hover:bg-gray-500/5"
											}`}
											onClick={() => handleRequestSelection(reqObj)}
										>
											{isSelected && (
												<span className="absolute left-0 top-0 h-full w-1 rounded-l-md bg-primary" />
											)}
											<div className="pl-1 flex">
												<div className="w-1/2 flex flex-col justify-between">
													<p
														className={` text-3xl leading-tight font-bold tracking-wider ${
															isSelected ? "text-primary" : "text-darkGreen"
														}`}
													>
														{reqObj.reqnum}
													</p>
													<div className="mt-1 flex flex-wrap items-center gap-1.5">
														<span
															className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xxs font-semibold ${programMeta.className}`}
														>
															{programMeta.label}
														</span>
														<span
															className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xxs font-semibold ${statusMeta.className}`}
														>
															{statusMeta.label}
														</span>
													</div>
												</div>
												<div className="ml-3  flex flex-col w-1/2 items-end ">
													{access === "ADMIN" && reqObj.instructor_name && (
														<p className="mt-1 text-sm text-gray-700 font-medium truncate">
															{reqObj.instructor_name}
														</p>
													)}

													{reqObj.coursetitle && (
														<p className="mt-1 text-sm text-gray-600 font-medium truncate">
															{reqObj.coursetitle}
														</p>
													)}
													{reqObj.rqdate && (
														<p className="mt-1 text-xs text-gray-700">
															{dayjs(reqObj.rqdate).format("MM/DD/YYYY h:mm A")}
														</p>
													)}
												</div>
											</div>
										</button>
									);
								})}

								{/* Pagination info and loading indicator */}
								{!searchMode && !isStringSearch && (
									<div className="text-center text-sm text-gray-500 py-2">
										{loading && !initialLoad && (
											<div className="mb-2">Loading more requests...</div>
										)}
										<div>
											Showing {displayRequests.length} of {totalCount} requests
											{hasNext && <span> (scroll for more)</span>}
										</div>
										{totalPages > 1 && (
											<div className="text-xs mt-1">
												Page {currentPage + 1} of {totalPages}
											</div>
										)}
									</div>
								)}
								{isStringSearch && (
									<div className="text-center text-sm text-gray-500 py-2">
										Showing {displayRequests.length} match
										{displayRequests.length === 1 ? "" : "es"} in the latest 100 requests
									</div>
								)}
							</>
						) : (
							<div className="text-center text-gray-500 py-8">
								{searchQuery || hasQuickFilters ? (
									<>
										<p>
											{searchQuery
												? `No requests found matching "${searchQuery}"`
												: "No requests found for the selected filters."}
										</p>
										{searchQuery && (
											<button
												onClick={clearSearch}
												className="mt-2 text-blue-600 hover:text-blue-800 underline"
											>
												Clear search to see all requests
											</button>
										)}
										{hasQuickFilters && (
											<button
												type="button"
												onClick={clearQuickFilters}
												className=" text-center my-auto h-full text-blue-600 hover:text-blue-800 underline"
											>
												Clear filters
											</button>
										)}
									</>
								) : (
									"No OMR Requests found."
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
